/**
 * F24: il modulo, non il pagamento.
 *
 * Finché un versamento era solo una riga di estratto conto, compariva nei
 * numeri il giorno in cui i soldi erano già usciti. Registrarlo qui, con la sua
 * scadenza, è ciò che permette al previsionale di dirlo **prima** — l'unico
 * momento in cui l'informazione serve a decidere.
 */
import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { api, type Categoria, type Movimento, type RispostaMovimenti } from '../api';
import {
  Campo,
  CampoImporto,
  Caricamento,
  Dialogo,
  Errore,
  Etichetta,
  Importo,
  Scheda,
  Vuoto,
  useAlberoCategorie,
  useAvvisi,
  useCarico,
  useDebounce,
} from '../componenti/base';
import { formattaData, formattaEuro, plurale } from '../formato';
import { useSessione } from '../main';

const SEZIONI = [
  ['ERARIO', 'Erario'],
  ['INPS', 'INPS'],
  ['REGIONI', 'Regioni'],
  ['TRIBUTI_LOCALI', 'Tributi locali'],
  ['ALTRI_ENTI', 'Altri enti'],
  ['ACCISE', 'Accise'],
] as const;

type Sezione = (typeof SEZIONI)[number][0];
type Stato = 'DA_VERSARE' | 'VERSATO' | 'ANNULLATO';

interface Tributo {
  id?: string;
  sezione: Sezione;
  codice: string;
  riferimento: string | null;
  rateazione: string | null;
  debitoCent: number;
  creditoCent: number;
}

interface F24 {
  id: string;
  descrizione: string;
  dataScadenza: string;
  stato: Stato;
  dataVersamento: string | null;
  debitoCent: number;
  creditoCent: number;
  saldoCent: number;
  categoriaId: string | null;
  protocollo: string | null;
  note: string | null;
  categoria: { id: string; nome: string } | null;
  movimento: { id: string; data: string; importoCent: number; dicitura: string } | null;
  tributi: Tributo[];
}

export function F24Pagina() {
  const { organizzazione } = useSessione();
  const oggi = new Date().toISOString().slice(0, 10);
  const elenco = useCarico(() => api.get<F24[]>('/api/f24'), []);
  const categorie = useCarico(() => api.get<Categoria[]>('/api/categorie'), []);
  const [inModifica, setInModifica] = useState<F24 | null>(null);
  const [nuovo, setNuovo] = useState(false);
  const [daVersare, setDaVersare] = useState<F24 | null>(null);
  const avvisa = useAvvisi();

  const tutti = elenco.dati ?? [];
  const attesi = tutti.filter((f) => f.stato === 'DA_VERSARE');
  const attesiCent = attesi.reduce((t, f) => t + f.saldoCent, 0);
  const scadutiCent = attesi.filter((f) => f.dataScadenza < oggi).reduce((t, f) => t + f.saldoCent, 0);

  const elimina = async (f: F24) => {
    if (!confirm(`Eliminare «${f.descrizione}»?\n\nSparisce anche dalla previsione di cassa.`)) return;
    try {
      await api.del(`/api/f24/${f.id}`);
      avvisa('F24 eliminato.');
      elenco.ricarica();
    } catch (e) {
      avvisa(e instanceof Error ? e.message : 'Eliminazione non riuscita.', 'errore');
    }
  };

  const annullaVersamento = async (f: F24) => {
    try {
      await api.post(`/api/f24/${f.id}/annulla-versamento`, {});
      avvisa('Versamento annullato: l’F24 torna nella previsione di cassa.');
      elenco.ricarica();
    } catch (e) {
      avvisa(e instanceof Error ? e.message : 'Operazione non riuscita.', 'errore');
    }
  };

  return (
    <div className="pagina space-y-5">
      <header>
        <h1 className="text-lg font-semibold tracking-tight">F24</h1>
        <p className="text-sm text-inchiostro-500">
          Un F24 non è un pagamento: è un modulo con più tributi, spesso compensati da crediti. Registrarlo con la
          sua scadenza lo fa entrare nella{' '}
          <Link to="/previsionale" className="underline underline-offset-2">
            previsione di cassa
          </Link>{' '}
          <strong>prima</strong> che i soldi escano. A pesare è il saldo, non il lordo dei tributi.
        </p>
      </header>

      <div className="grid gap-3 sm:grid-cols-3">
        <Scheda>
          <p className="text-xs uppercase tracking-wide text-inchiostro-500">Da versare</p>
          <p className="mt-1 text-xl font-semibold">
            <Importo cent={-attesiCent} tono="uscite" />
          </p>
          <p className="mt-0.5 text-xs text-inchiostro-500">{plurale(attesi.length, 'modulo', 'moduli')}</p>
        </Scheda>
        <Scheda>
          <p className="text-xs uppercase tracking-wide text-inchiostro-500">Di cui già scaduti</p>
          <p className="mt-1 text-xl font-semibold">
            {scadutiCent > 0 ? <Importo cent={-scadutiCent} tono="uscite" /> : <span>—</span>}
          </p>
          <p className="mt-0.5 text-xs text-inchiostro-500">
            {scadutiCent > 0 ? 'Nel previsionale pesano su oggi' : 'Nessuna scadenza arretrata'}
          </p>
        </Scheda>
        <Scheda>
          <p className="text-xs uppercase tracking-wide text-inchiostro-500">Registrati in tutto</p>
          <p className="mt-1 text-xl font-semibold">{tutti.length}</p>
          <p className="mt-0.5 text-xs text-inchiostro-500">
            {tutti.filter((f) => f.stato === 'VERSATO').length} versati
          </p>
        </Scheda>
      </div>

      <Scheda senzaPadding>
        <div className="flex items-center justify-between border-b border-inchiostro-200 p-3">
          <span className="text-sm text-inchiostro-500">{plurale(tutti.length, 'F24 registrato', 'F24 registrati')}</span>
          <button className="bottone-primario" onClick={() => setNuovo(true)}>
            Nuovo F24
          </button>
        </div>

        {elenco.caricamento ? (
          <Caricamento />
        ) : elenco.errore ? (
          <div className="p-4">
            <Errore testo={elenco.errore} riprova={elenco.ricarica} />
          </div>
        ) : tutti.length === 0 ? (
          <Vuoto
            titolo="Nessun F24 registrato"
            testo="Registra il prossimo versamento con la sua scadenza: comparirà nella previsione di cassa prima di uscire dal conto."
          />
        ) : (
          <div className="overflow-x-auto">
            <table className="dati">
              <thead>
                <tr>
                  <th>Scadenza</th>
                  <th>Descrizione</th>
                  <th>Voce</th>
                  <th className="text-right">Tributi</th>
                  <th className="text-right">Compensato</th>
                  <th className="text-right">Da versare</th>
                  <th>Stato</th>
                  <th className="w-56" />
                </tr>
              </thead>
              <tbody>
                {tutti.map((f) => {
                  const scaduto = f.stato === 'DA_VERSARE' && f.dataScadenza < oggi;
                  return (
                    <tr key={f.id} className={f.stato === 'ANNULLATO' ? 'opacity-50' : ''}>
                      <td className="num whitespace-nowrap">
                        {formattaData(f.dataScadenza, organizzazione.formatoData)}
                        {scaduto && (
                          <span className="ml-2">
                            <Etichetta
                              testo="scaduto"
                              tono="allerta"
                              titolo="Nel previsionale pesa su oggi: è un’uscita che deve ancora avvenire."
                            />
                          </span>
                        )}
                      </td>
                      <td className="font-medium">
                        {f.descrizione}
                        {f.tributi.length > 0 && (
                          <span className="mt-0.5 block text-xs font-normal text-inchiostro-500">
                            {f.tributi.map((t) => t.codice).join(' · ')}
                          </span>
                        )}
                      </td>
                      <td className="text-xs">{f.categoria?.nome ?? '—'}</td>
                      <td className="text-right">
                        <Importo cent={f.debitoCent} />
                      </td>
                      <td className="text-right">
                        {f.creditoCent > 0 ? (
                          <Importo cent={f.creditoCent} tono="incassi" />
                        ) : (
                          <span className="text-inchiostro-300">—</span>
                        )}
                      </td>
                      <td className="text-right font-medium">
                        <Importo cent={f.saldoCent} />
                      </td>
                      <td>
                        {f.stato === 'VERSATO' ? (
                          <>
                            <Etichetta testo="versato" tono="ok" />
                            {f.movimento ? (
                              <span
                                className="mt-0.5 block max-w-44 truncate text-xs text-inchiostro-500"
                                title={f.movimento.dicitura}
                              >
                                {f.movimento.dicitura}
                              </span>
                            ) : (
                              <span className="mt-0.5 block text-xs text-attenzione">addebito non collegato</span>
                            )}
                          </>
                        ) : f.stato === 'ANNULLATO' ? (
                          <Etichetta testo="annullato" />
                        ) : (
                          <Etichetta testo="da versare" tono="attenzione" />
                        )}
                      </td>
                      <td className="text-right">
                        <div className="flex justify-end gap-3 whitespace-nowrap">
                          {f.stato === 'DA_VERSARE' ? (
                            <button
                              className="text-xs underline underline-offset-2"
                              onClick={() => setDaVersare(f)}
                              title="Da previsione a fatto, collegando l’addebito in banca."
                            >
                              Segna versato
                            </button>
                          ) : (
                            <button
                              className="text-xs underline underline-offset-2"
                              onClick={() => annullaVersamento(f)}
                            >
                              Annulla versamento
                            </button>
                          )}
                          <button className="text-xs underline underline-offset-2" onClick={() => setInModifica(f)}>
                            Modifica
                          </button>
                          <button
                            className="text-xs text-allerta underline underline-offset-2"
                            onClick={() => elimina(f)}
                          >
                            Elimina
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Scheda>


      {(nuovo || inModifica) && (
        <DialogoF24
          f24={inModifica}
          categorie={categorie.dati ?? []}
          onChiudi={() => {
            setNuovo(false);
            setInModifica(null);
          }}
          onFatto={() => {
            setNuovo(false);
            setInModifica(null);
            elenco.ricarica();
          }}
        />
      )}

      {daVersare && (
        <DialogoVersamento
          f24={daVersare}
          onChiudi={() => setDaVersare(null)}
          onFatto={() => {
            setDaVersare(null);
            elenco.ricarica();
          }}
        />
      )}
    </div>
  );
}

const vuoto = (): Tributo => ({
  sezione: 'ERARIO',
  codice: '',
  riferimento: '',
  rateazione: '',
  debitoCent: 0,
  creditoCent: 0,
});

/** Da euro digitati a centesimi interi, senza passare per i decimali. */
function inCent(testo: string): number {
  const pulito = testo.replace(/\s|€/g, '').replace(/\./g, '').replace(',', '.');
  if (!pulito) return 0;
  const n = Number(pulito);
  return Number.isFinite(n) ? Math.round(n * 100) : 0;
}
const inEuro = (cent: number) => (cent === 0 ? '' : (cent / 100).toFixed(2).replace('.', ','));

function DialogoF24({
  f24,
  categorie,
  onChiudi,
  onFatto,
}: {
  f24: F24 | null;
  categorie: Categoria[];
  onChiudi: () => void;
  onFatto: () => void;
}) {
  const avvisa = useAvvisi();
  const { piatto } = useAlberoCategorie(categorie);
  const uscite = piatto.filter((p) => p.categoria.verso === 'USCITA' && !p.categoria.sistema);

  const [descrizione, setDescrizione] = useState(f24?.descrizione ?? '');
  const [dataScadenza, setDataScadenza] = useState(f24?.dataScadenza ?? '');
  const [categoriaId, setCategoriaId] = useState(f24?.categoriaId ?? '');
  const [protocollo, setProtocollo] = useState(f24?.protocollo ?? '');
  const [note, setNote] = useState(f24?.note ?? '');
  /**
   * Il dettaglio dei tributi è **facoltativo**. Pretenderlo per trenta moduli
   * l'anno, quando ciò che serve alla cassa è una data e un importo, sarebbe un
   * pedaggio — e chi non lo paga resterebbe senza previsione.
   */
  const [conDettaglio, setConDettaglio] = useState((f24?.tributi.length ?? 0) > 0);
  const [saldo, setSaldo] = useState(f24 && f24.tributi.length === 0 ? inEuro(f24.saldoCent) : '');
  const [tributi, setTributi] = useState<Tributo[]>(
    f24?.tributi.length ? f24.tributi.map((t) => ({ ...t })) : [vuoto()],
  );
  const [attesa, setAttesa] = useState(false);

  const totali = useMemo(() => {
    if (!conDettaglio) {
      const s = inCent(saldo);
      return { debitoCent: s, creditoCent: 0, saldoCent: s };
    }
    const debitoCent = tributi.reduce((t, r) => t + r.debitoCent, 0);
    const creditoCent = tributi.reduce((t, r) => t + r.creditoCent, 0);
    return { debitoCent, creditoCent, saldoCent: debitoCent - creditoCent };
  }, [conDettaglio, saldo, tributi]);

  const righeValide = tributi.filter((t) => t.codice.trim() && (t.debitoCent > 0 || t.creditoCent > 0));
  const puoSalvare =
    descrizione.trim().length > 0 &&
    dataScadenza.length === 10 &&
    totali.saldoCent >= 0 &&
    (conDettaglio ? righeValide.length > 0 : totali.saldoCent > 0);

  const cambia = (i: number, patch: Partial<Tributo>) =>
    setTributi((v) => v.map((t, j) => (j === i ? { ...t, ...patch } : t)));

  const salva = async () => {
    setAttesa(true);
    try {
      const corpo = {
        descrizione: descrizione.trim(),
        dataScadenza,
        categoriaId: categoriaId || null,
        protocollo: protocollo.trim() || null,
        note: note.trim() || null,
        ...(conDettaglio
          ? {
              tributi: righeValide.map((t) => ({
                sezione: t.sezione,
                codice: t.codice.trim(),
                riferimento: t.riferimento?.trim() || null,
                rateazione: t.rateazione?.trim() || null,
                debitoCent: t.debitoCent,
                creditoCent: t.creditoCent,
              })),
            }
          : { saldoCent: totali.saldoCent }),
      };
      if (f24) await api.put(`/api/f24/${f24.id}`, corpo);
      else await api.post('/api/f24', corpo);
      avvisa(f24 ? 'F24 aggiornato.' : 'F24 registrato: da ora pesa sulla previsione di cassa alla sua scadenza.');
      onFatto();
    } catch (e) {
      avvisa(e instanceof Error ? e.message : 'Salvataggio non riuscito.', 'errore');
    } finally {
      setAttesa(false);
    }
  };

  return (
    <Dialogo aperto titolo={f24 ? 'Modifica l’F24' : 'Nuovo F24'} onChiudi={onChiudi} larghezza="max-w-4xl">
      <div className="space-y-4">
        <div className="flex flex-wrap gap-3">
          <Campo etichetta="Descrizione" larghezza="w-72">
            <input
              className="campo"
              value={descrizione}
              onChange={(e) => setDescrizione(e.target.value)}
              placeholder="Es. F24 luglio 2026"
            />
          </Campo>
          <Campo
            etichetta="Scadenza"
            larghezza="w-44"
            aiuto="La data del versamento, non quella dell’addebito: è questa a pesare sul previsionale."
          >
            <input
              type="date"
              className="campo"
              value={dataScadenza}
              onChange={(e) => setDataScadenza(e.target.value)}
            />
          </Campo>
          <Campo etichetta="Voce del previsionale" larghezza="w-64">
            <select className="campo" value={categoriaId} onChange={(e) => setCategoriaId(e.target.value)}>
              <option value="">Imposte (predefinita)</option>
              {uscite.map(({ categoria, livello }) => (
                <option key={categoria.id} value={categoria.id}>
                  {' '.repeat(livello * 3)}
                  {categoria.nome}
                </option>
              ))}
            </select>
          </Campo>
        </div>

        <label className="flex items-start gap-2 text-sm">
          <input
            type="checkbox"
            className="mt-0.5"
            checked={conDettaglio}
            onChange={(e) => setConDettaglio(e.target.checked)}
          />
          <span>
            Compila i tributi riga per riga
            <span className="mt-0.5 block text-xs text-inchiostro-500">
              Serve per leggere la spesa fiscale per natura. Se ti basta l’effetto di cassa, lascialo spento e indica
              solo l’importo.
            </span>
          </span>
        </label>

        {!conDettaglio ? (
          <Campo etichetta="Importo da versare" larghezza="w-44">
            <input
              className="campo num text-right"
              value={saldo}
              onChange={(e) => setSaldo(e.target.value)}
              placeholder="0,00"
            />
          </Campo>
        ) : (
          <div className="overflow-x-auto">
            <table className="dati">
              <thead>
                <tr>
                  <th className="w-36">Sezione</th>
                  <th className="w-28">Codice</th>
                  <th className="w-28">Riferimento</th>
                  <th className="w-28 text-right">A debito</th>
                  <th className="w-28 text-right">A credito</th>
                  <th className="w-10" />
                </tr>
              </thead>
              <tbody>
                {tributi.map((t, i) => (
                  <tr key={i}>
                    <td>
                      <select
                        className="campo"
                        value={t.sezione}
                        onChange={(e) => cambia(i, { sezione: e.target.value as Sezione })}
                      >
                        {SEZIONI.map(([v, n]) => (
                          <option key={v} value={v}>
                            {n}
                          </option>
                        ))}
                      </select>
                    </td>
                    <td>
                      <input
                        className="campo"
                        aria-label={`Codice tributo riga ${i + 1}`}
                        value={t.codice}
                        onChange={(e) => cambia(i, { codice: e.target.value })}
                        placeholder="6006"
                      />
                    </td>
                    <td>
                      <input
                        className="campo"
                        aria-label={`Riferimento riga ${i + 1}`}
                        value={t.riferimento ?? ''}
                        onChange={(e) => cambia(i, { riferimento: e.target.value })}
                        placeholder="0007/2026"
                      />
                    </td>
                    <td>
                      {/* `CampoImporto` e non un input crudo: tenendo i
                          centesimi nello stato e riformattandoli a ogni tasto,
                          i decimali non si riuscivano a digitare. */}
                      <CampoImporto
                        ariaEtichetta={`Importo a debito riga ${i + 1}`}
                        valoreCent={t.debitoCent}
                        onCambia={(cent) => cambia(i, { debitoCent: cent })}
                      />
                    </td>
                    <td>
                      <CampoImporto
                        ariaEtichetta={`Importo a credito riga ${i + 1}`}
                        valoreCent={t.creditoCent}
                        onCambia={(cent) => cambia(i, { creditoCent: cent })}
                      />
                    </td>
                    <td>
                      {tributi.length > 1 && (
                        <button
                          className="text-allerta"
                          aria-label={`Togli la riga ${i + 1}`}
                          onClick={() => setTributi((v) => v.filter((_, j) => j !== i))}
                        >
                          ×
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <button className="bottone-secondario mt-2" onClick={() => setTributi((v) => [...v, vuoto()])}>
              Aggiungi un tributo
            </button>
          </div>
        )}

        <Campo etichetta="Note" larghezza="w-full">
          <input className="campo" value={note} onChange={(e) => setNote(e.target.value)} />
        </Campo>

        {/* Il saldo è ciò che esce di cassa, e va visto mentre si compila: è il
            numero che finirà nel previsionale, non il lordo dei tributi. */}
        <div className="rounded-md bg-inchiostro-50 p-3 text-sm">
          Tributi <span className="num font-medium">{formattaEuro(totali.debitoCent)}</span>
          {totali.creditoCent > 0 && (
            <>
              {' '}
              − compensati <span className="num font-medium">{formattaEuro(totali.creditoCent)}</span>
            </>
          )}{' '}
          = <span className="num font-semibold">{formattaEuro(totali.saldoCent)}</span> che escono di cassa
          {totali.saldoCent < 0 && (
            <span className="ml-2 text-allerta">
              I crediti superano i tributi: un F24 non può chiudere in negativo.
            </span>
          )}
        </div>

        <div className="flex justify-end gap-2">
          <button className="bottone-secondario" onClick={onChiudi}>
            Annulla
          </button>
          <button className="bottone-primario" onClick={salva} disabled={!puoSalvare || attesa}>
            {attesa ? 'Salvataggio…' : f24 ? 'Salva' : 'Registra l’F24'}
          </button>
        </div>
      </div>
    </Dialogo>
  );
}

/**
 * Da previsione a fatto. L'addebito in banca è facoltativo — può arrivare
 * qualche giorno dopo — ma collegarlo toglie il movimento dalla riconciliazione
 * e chiude il giro senza doverlo convalidare a parte.
 */
function DialogoVersamento({ f24, onChiudi, onFatto }: { f24: F24; onChiudi: () => void; onFatto: () => void }) {
  const { organizzazione } = useSessione();
  const avvisa = useAvvisi();
  const [data, setData] = useState(f24.dataScadenza);
  const [movimentoId, setMovimentoId] = useState('');
  const [cerca, setCerca] = useState('');
  const cercaRitardata = useDebounce(cerca);
  const [attesa, setAttesa] = useState(false);

  const query = new URLSearchParams({ limite: '40', daRiconciliare: 'true' });
  if (cercaRitardata) query.set('cerca', cercaRitardata);
  const movimenti = useCarico(
    () => api.get<RispostaMovimenti>(`/api/movimenti?${query}`),
    [cercaRitardata],
  );
  // Un F24 è un'uscita: proporre accrediti sarebbe solo rumore.
  const candidati = (movimenti.dati?.movimenti ?? []).filter((m: Movimento) => m.importoCent < 0);

  const conferma = async () => {
    setAttesa(true);
    try {
      await api.post(`/api/f24/${f24.id}/versa`, { dataVersamento: data, movimentoId: movimentoId || null });
      avvisa(
        movimentoId
          ? 'F24 versato e addebito collegato: il movimento esce dalla riconciliazione.'
          : 'F24 versato. L’addebito in banca si può collegare più avanti.',
      );
      onFatto();
    } catch (e) {
      avvisa(e instanceof Error ? e.message : 'Operazione non riuscita.', 'errore');
    } finally {
      setAttesa(false);
    }
  };

  return (
    <Dialogo aperto titolo="Segna versato" onChiudi={onChiudi} larghezza="max-w-3xl">
      <div className="mb-3 rounded-md bg-inchiostro-50 p-3 text-sm">
        <p className="font-medium">{f24.descrizione}</p>
        <p className="mt-0.5 text-inchiostro-600">
          Scadenza {formattaData(f24.dataScadenza, organizzazione.formatoData)} ·{' '}
          <span className="num font-medium">{formattaEuro(f24.saldoCent)}</span>
        </p>
      </div>

      <div className="flex flex-wrap items-end gap-3">
        <Campo etichetta="Data del versamento" larghezza="w-44">
          <input type="date" className="campo" value={data} onChange={(e) => setData(e.target.value)} />
        </Campo>
        <Campo etichetta="Cerca l’addebito" larghezza="w-72">
          <input
            className="campo"
            value={cerca}
            onChange={(e) => setCerca(e.target.value)}
            placeholder="Es. Delega Unificata"
          />
        </Campo>
      </div>

      <p className="mt-3 text-xs text-inchiostro-500">
        L’addebito in banca è facoltativo: se non è ancora arrivato, lascia la scelta vuota e collegalo più avanti.
      </p>

      <div className="mt-2 max-h-[38vh] overflow-y-auto border-y border-inchiostro-200">
        {movimenti.caricamento ? (
          <Caricamento />
        ) : candidati.length === 0 ? (
          <Vuoto titolo="Nessun addebito da collegare" testo="Cerca per causale, oppure prosegui senza collegarlo." />
        ) : (
          <table className="dati">
            <tbody>
              {candidati.map((m: Movimento) => {
                const preso = movimentoId === m.id;
                const esatto = Math.abs(m.importoCent) === f24.saldoCent;
                return (
                  <tr key={m.id} className={preso ? 'bg-inchiostro-50' : ''}>
                    <td className="w-10">
                      <input
                        type="radio"
                        name="addebito"
                        aria-label={`Collega ${m.dicitura}`}
                        checked={preso}
                        onChange={() => setMovimentoId(m.id)}
                      />
                    </td>
                    <td className="num whitespace-nowrap">{formattaData(m.data, organizzazione.formatoData)}</td>
                    <td className="max-w-80 truncate" title={m.dicitura}>
                      {m.dicitura}
                      {esatto && (
                        <span className="ml-2">
                          <Etichetta testo="importo esatto" tono="ok" />
                        </span>
                      )}
                    </td>
                    <td className="text-right">
                      <Importo cent={m.importoCent} tono="auto" />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      <div className="mt-4 flex justify-end gap-2">
        {movimentoId && (
          <button className="bottone-secondario" onClick={() => setMovimentoId('')}>
            Non collegare
          </button>
        )}
        <button className="bottone-secondario" onClick={onChiudi}>
          Annulla
        </button>
        <button className="bottone-primario" onClick={conferma} disabled={attesa || data.length !== 10}>
          {attesa ? 'Registro…' : 'Segna versato'}
        </button>
      </div>
    </Dialogo>
  );
}
